import { useState, useEffect } from "react";
import DateSelector from "../../components/shared/fields/DateSelector";
import Datatable from "../../components/shared/datatable/Datatable";
import Button from "../../components/shared/buttons/button";
import { Col, Row } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import { convertToUserTimezone, getCurrentDate, makePriorLotNumber } from "../../helpers/Utils";
import { DEFAULT_DATETIME_FORMAT } from "../../helpers/constants";
import { useGetPastingLogsQuery, useGetPastingSummaryQuery } from "./mixingApi";
import GridCard from "../../components/shared/gridCard/GridCard";
import KgGreen from "../../images/kg_green.svg";
import PastingModal from "./PastingModal";

const Pasting = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));
  const [page, setPage] = useState(1);

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalMode, setModalMode] = useState("edit");

  const pastingLogLimit = 10;
  const { data: logsData, isLoading, isFetching } = useGetPastingLogsQuery({ page, date, per_page: pastingLogLimit }, { refetchOnMountOrArgChange: true });
  const { data: summaryData } = useGetPastingSummaryQuery({ batch_code: lotNumber }, { skip: !lotNumber });

  const listItems = [
    { label: _t("total_weight", "Total Weight"), value: summaryData?.data?.total_weight ?? 0, icon: KgGreen },
    { label: _t("packed_weight", "Weight Packed"), value: summaryData?.data?.packed_weight ?? 0, icon: KgGreen },
    { label: _t("unpacked_weight", "Weight Not Packed"), value: summaryData?.data?.unpacked_weight ?? 0, icon: KgGreen },
    { label: _t("total_boxes", "Total Boxes"), value: summaryData?.data?.total_boxes ?? 0, icon: KgGreen },
  ];

  const handleEdit = (row) => {
    setSelectedRecord(row);
    setModalMode(row.status === "packed" ? "view" : "edit");
    setShowModal(true);
  };

  const columns = [
    { label: _t("entered_date", "Entered Date"), accessor: "created_at", cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "" },
    { label: _t("batch_code", "Batch Code"), accessor: "batch_code", cell: ({ value }) => value ? `#${value}` : "" },
    { label: _t("total_weight", "Total Weight"), accessor: "total_weight" },
    { label: _t("packed_weight", "Weight Packed"), accessor: "packed_weight", cell: ({ value }) => value ?? "---" },
    { label: _t("unpacked_weight", "Weight Not Packed"), accessor: "unpacked_weight", cell: ({ value }) => value ?? "---" },
    { label: _t("total_boxes", "No of Boxes"), accessor: "total_boxes", cell: ({ value }) => value ?? "---" },
    { label: _t("lot_completion_percent", "Lot Completion %"), accessor: "lot_completion_percent", cell: ({ value }) => value ?? "---" },
    {
      label: _t("status", "Status"), accessor: "status",
      cell: ({ value }) => (
        <span className={value === "packed" ? "cmn_badge" : "cmn_badge inactive"}>
          {value === "packed" ? _t("status_packed", "Packed") : value === "mixing" ? _t("status_mixing", "Mixing") : value}
        </span>
      ),
    },
    { label: _t("status_updated_at", "Status Updated At"), accessor: "status_updated_at", cell: ({ value }) => value ? convertToUserTimezone(value, DEFAULT_DATETIME_FORMAT) : "" },
    {
      label: _t("action", "Action"), accessor: "id",
      cell: ({ row }) => <Button size="small" className={row.status === "packed" ? "borderRedBtn" : "" } label={row.status === "packed" ? _t("view", "View") : _t("edit", "Edit")} onClick={() => handleEdit(row)} />,
    },
  ];

  return (
    <div>
      <PastingModal
        show={showModal}
        setShow={setShowModal}
        record={selectedRecord}
        mode={modalMode}
      />
      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("pasting_logs", "Pasting Logs")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard key={index} title={list.label} value={list.value} icon={list.icon} />
          ))}
        </ul>
      </div>

      <Datatable
        title={_t("pasting_logs", "Pasting Logs")}
        columns={columns}
        tableData={logsData?.data}
        isLoading={isLoading || isFetching}
        showPegination={true}
        page={page}
        onPageChange={setPage}
        showCount={true}
        pageLimit={pastingLogLimit}
      />
    </div>
  );
};

export default Pasting;
